/**
 * Persistence for the Auth0 refresh token — a high-value durable credential,
 * deliberately kept OUT of `window.MetaShipGlobalState` / the `vuex` mirror
 * (mirrors legacy's `setAuth0RefreshToken` action, which is NOT pushed to
 * `updateExternalState`).
 */
import { refreshAuth0Tokens } from "./auth0";
import { getBridgeToken, type BridgeTokenResult } from "./identity";

const REFRESH_TOKEN_KEY = "metaship:auth0RefreshToken";
const REFRESH_LOCK_NAME = "metaship:auth0-refresh";
const AUTH_SOURCE_KEY = "metaship:authSource";

/**
 * Which system signed this browser in (dual-run rule R5). Refresh and resume
 * use the same source: the Auth0 refresh token, or `GET /identity/token`.
 * `null` = not known. An older session with only an Auth0 refresh token reads
 * as Auth0, which is what it is.
 */
export type AuthSource = "auth0" | "identity";

export function getAuthSource(): AuthSource | null {
  try {
    const value = localStorage.getItem(AUTH_SOURCE_KEY);
    return value === "auth0" || value === "identity" ? value : null;
  } catch {
    return null;
  }
}

export function setAuthSource(source: AuthSource | null): void {
  try {
    if (source) localStorage.setItem(AUTH_SOURCE_KEY, source);
    else localStorage.removeItem(AUTH_SOURCE_KEY);
  } catch {
    // best-effort
  }
}

export function getStoredAuth0RefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredAuth0RefreshToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // best-effort
  }
}

/**
 * JWTs are base64URL (RFC 7515 §2: `-`/`_`, no padding) — plain `atob()`
 * throws on `-`/`_` (it only accepts standard `+`/`/` base64), so a payload
 * segment that happens to contain either character (common — it's an
 * arbitrary-byte encoding of arbitrary JSON) silently failed to decode
 * before this translated the alphabet back. Also decodes through
 * `TextDecoder` rather than reading `atob()`'s output directly as JSON text
 * — `atob` yields a Latin-1 binary string, which mangles any non-ASCII
 * character in the payload (e.g. an accented firstname/surname) into
 * mojibake if parsed as-is.
 */
function decodeJwtPayload(bearer: string | null | undefined): Record<string, unknown> | null {
  try {
    const raw = (bearer || "").replace(/^Bearer\s+/i, "");
    const segment = raw.split(".")[1];
    if (!segment) return null;
    let base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) base64 += "=";
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder("utf-8").decode(bytes));
  } catch {
    return null;
  }
}

/** Decode a bearer JWT's `exp` claim to a millisecond epoch, or `null` if it can't be read. */
export function decodeJwtExpiryMs(bearer: string | null | undefined): number | null {
  const payload = decodeJwtPayload(bearer);
  return typeof payload?.exp === "number" ? payload.exp * 1000 : null;
}

export interface MetaShipTokenUser {
  id: number;
  firstname: string;
  surname: string;
  emailAddress: string;
  tagId: number | null;
}

/**
 * Decode the numeric Metaship identity out of a minted Metaship JWT — the
 * SAME `{ id, firstname, surname, emailAddress, tagId, userID }` payload
 * `refreshUserToken` signs (`metaship-backend/Services/UserAccess/
 * user-access-controller.ts:209-216`). The Auth0 id_token alone never carries
 * this — only a token minted via `POST /user-access/refresh-token` does.
 */
export function decodeMetaShipUser(bearer: string | null | undefined): MetaShipTokenUser | null {
  const payload = decodeJwtPayload(bearer);
  if (!payload || typeof payload.id !== "number") return null;
  return {
    id: payload.id,
    firstname: typeof payload.firstname === "string" ? payload.firstname : "",
    surname: typeof payload.surname === "string" ? payload.surname : "",
    emailAddress: typeof payload.emailAddress === "string" ? payload.emailAddress : "",
    tagId: typeof payload.tagId === "number" ? payload.tagId : null,
  };
}

export interface Auth0Identity {
  sub: string;
  email: string;
  firstname: string;
  surname: string;
  avatar: string | null;
  /** The token's `email_verified` claim; null when the token does not carry it. */
  emailVerified: boolean | null;
}

/**
 * Decode the identity claims out of an Auth0 id_token (`sub`, `email`,
 * `given_name`/`family_name`, `name`, `picture`) — the SAME fields
 * `handleRedirectCallback` reads off the userinfo response.
 *
 * Needed because a SILENT resume (`refreshAuth0TokensLocked` → a fresh
 * id_token, no callback) never went near that response, so the store had no
 * identity to show: the top bar rendered a "?" monogram, and `user.id` stayed
 * null — which also made `readLastActiveOrgId` a no-op, so a multi-org user
 * was asked to pick an organisation on EVERY visit.
 */
export function decodeAuth0Identity(idToken: string | null | undefined): Auth0Identity | null {
  const payload = decodeJwtPayload(idToken);
  if (!payload) return null;
  const sub = typeof payload.sub === "string" ? payload.sub : "";
  const email = typeof payload.email === "string" ? payload.email : "";
  if (!sub && !email) return null;
  const name = typeof payload.name === "string" ? payload.name : "";
  const given = typeof payload.given_name === "string" ? payload.given_name : "";
  const family = typeof payload.family_name === "string" ? payload.family_name : "";
  return {
    sub,
    email,
    firstname: given || name.split(" ")[0] || "",
    surname: family || name.split(" ").slice(1).join(" ") || "",
    avatar: typeof payload.picture === "string" ? payload.picture : null,
    emailVerified: typeof payload.email_verified === "boolean" ? payload.email_verified : null,
  };
}

/**
 * Refresh the Auth0 credential, serialised across every tab of this origin
 * via the Web Locks API (falls back to running unserialised where
 * unsupported — Safari < 15.4, or a non-browser test environment). Auth0
 * ROTATES refresh tokens and revokes the whole family on reuse, so two tabs
 * racing to refresh at once can otherwise kill the session for both. The
 * stored token is re-read AFTER the lock is acquired — another tab may have
 * already rotated it while this one was waiting.
 */
export async function refreshAuth0TokensLocked(): Promise<{ id_token: string | null; refresh_token: string | null }> {
  const run = async () => {
    const rt = getStoredAuth0RefreshToken();
    if (!rt) throw new Error("no Auth0 refresh token");
    const fresh = await refreshAuth0Tokens(rt);
    if (fresh.refresh_token) setStoredAuth0RefreshToken(fresh.refresh_token);
    return fresh;
  };
  if (typeof navigator === "undefined" || !("locks" in navigator) || !navigator.locks) {
    return run();
  }
  return navigator.locks.request(REFRESH_LOCK_NAME, run);
}

/**
 * The Better Auth bridge token, under the SAME cross-tab lock as the Auth0
 * refresh. A cookie session does not rotate, so the lock only stops two tabs
 * that wake together from sending two requests; `getBridgeToken` adds single
 * flight inside one tab.
 */
export async function refreshBridgeTokenLocked(): Promise<BridgeTokenResult | null> {
  if (typeof navigator === "undefined" || !("locks" in navigator) || !navigator.locks) {
    return getBridgeToken();
  }
  return navigator.locks.request(REFRESH_LOCK_NAME, () => getBridgeToken());
}
